/**
 * useAuthentication Hook
 * Manages user authentication state and operations
 */

import { useState, useCallback, useEffect } from 'react';
import { apiService } from '../services/api';
import type { User, LoginCredentials } from '../types/user';
import logger from '../utils/simpleLogger';

interface UseAuthenticationReturn {
  isAuthenticated: boolean;
  currentUser: User | null;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export function useAuthentication(): UseAuthenticationReturn {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Check if user is authenticated
   */
  const checkAuth = useCallback(async () => {
    setIsLoading(true);
    logger.info('🔐 [AUTH] checkAuth() called', {
      tokenPresent: !!localStorage.getItem('access_token'),
      timestamp: new Date().toISOString(),
    });
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        const response = await apiService.getCurrentUser();
        if (response && response.id) {
          setCurrentUser(response as User);
          setIsAuthenticated(true);
          logger.info('✅ [AUTH] User authenticated', { userId: response.id });
        } else {
          throw new Error('Invalid user data');
        }
      } else {
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    } catch (err) {
      logger.error('❌ [AUTH] Authentication check failed', err);
      setIsAuthenticated(false);
      setCurrentUser(null);
      localStorage.removeItem('access_token');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Login user
   */
  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    logger.info('🔐 [AUTH] login() called', {
      identifier: credentials.email,
      credentialType: 'email',
      passwordLength: credentials.password.length,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await apiService.login({
        identifier: credentials.email,
        password: credentials.password,
      });

      if (response.access_token && response.user) {
        // Token ya guardado por authService.login() → tokenManager.saveTokensToStorage()
        setCurrentUser(response.user as User);
        setIsAuthenticated(true);
        logger.info('✅ [AUTH] User logged in successfully', {
          userId: response.user.id,
          username: response.user.username,
          email: response.user.email,
        });
        return true;
      } else {
        throw new Error('Invalid response from login API');
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Login error';
      setError(errorMsg);
      logger.error('❌ [AUTH] login error', {
        message: errorMsg,
        stack: err?.stack,
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(() => {
    logger.info('🔐 [AUTH] User logging out', {
      userId: currentUser?.id,
      username: currentUser?.username,
    });
    localStorage.removeItem('access_token');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setError(null);
  }, [currentUser]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Check authentication on mount only
  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar

  return {
    isAuthenticated,
    currentUser,
    isLoading,
    error,
    login,
    logout,
    checkAuth,
    clearError,
  };
}
